// The `localStorage.profile` invariant.
//
// A dozen components read this key and assume it exists — the sidebar calls
// logout() when it can't, and easyPay / the excel modal / fund tabs / the
// support modal all do `JSON.parse(localStorage.getItem('profile')!)` and throw
// on null. That assumption is fine, but only if something guarantees it.
//
// Historically nothing did: the key is written by exactly one page,
// /auth/profile, and the SMS login happens to route through it. Every login
// path added since (mini-app auto-login, admin impersonate) skipped that page
// and left the app authenticated but unusable.
//
// So rather than teaching twelve consumers to tolerate a missing profile, this
// runs once at startup and makes the invariant true for *every* entry point,
// present and future: if there is a session, there is a profile.

import posthog from 'posthog-js'

import { getAuthProfile } from 'services/auth'

export const hasSession = (): boolean => {
    try {
        const stored = window.localStorage.getItem('user')
        return !!stored && stored !== 'null' && !!JSON.parse(stored)?.token
    } catch {
        return false
    }
}

export const hasProfile = (): boolean => {
    try {
        const stored = window.localStorage.getItem('profile')
        return !!stored && stored !== 'null' && !!JSON.parse(stored)
    } catch {
        return false
    }
}

/**
 * Fetch and cache the profile, mirroring what the /auth/profile page does —
 * including its identify() call, so a session started anywhere maps to the
 * same PostHog person as one started on the web.
 *
 * Returns whether the profile carries a name; a member created from a shared
 * contact has none yet and still needs the form.
 */
export const fetchAndCacheProfile = async (): Promise<boolean> => {
    const profile = await getAuthProfile()
    window.localStorage.setItem('profile', JSON.stringify(profile))
    posthog.identify(profile?.phone_number, {
        user_id: profile?.id,
        name: profile?.first_name + ' ' + profile?.last_name,
        email: profile?.email,
        phone_number: profile?.phone_number,
    })
    return !!profile?.first_name?.length
}

/**
 * Guarantee the invariant before the first render.
 *
 * Awaited rather than backgrounded on purpose: consumers read the key
 * synchronously while rendering, so a background fetch would race them and
 * turn a deterministic bug into an intermittent one.
 *
 * Costs nothing in the common case — a web user who logged in normally already
 * has the key, and a logged-out visitor has no session to complete.
 *
 * Returns the path we should start on, or null to leave routing alone.
 */
export const ensureSessionProfile = async (): Promise<string | null> => {
    if (!hasSession() || hasProfile()) return null

    let named: boolean
    try {
        named = await fetchAndCacheProfile()
    } catch {
        // Token rejected, or the network is down. Leave it alone: the first
        // API call will 401 and the interceptor routes to the SMS login.
        return null
    }

    // No name yet (typically a member created from a shared contact). Send them
    // through the same form the SMS flow would have shown — the normal path,
    // not a special case.
    return named ? null : '/auth/profile'
}
